import type { ReactNode } from 'react'

// ============================================================================
// THE DAY PAGE'S GLYPHS — transcribed path-for-path from docs/design/handoff/day-page/*.dc.html.
// Same idiom as components/icons.tsx: viewBox 0 0 24 24, stroke = currentColor, round caps and
// joins, colour from a token class on the caller. The boards draw them at 1.8.
// ============================================================================

export interface DayIconProps {
  size?: number
  strokeWidth?: number
  className?: string
}

function G({ size = 16, strokeWidth = 1.8, className, children }: DayIconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ? `flex-none ${className}` : 'flex-none'}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** The Day page: a sun on the horizon. The rail entry and the Needs you today header. */
export const Sunrise = (p: DayIconProps) => (
  <G {...p}>
    <path d="M3 18h18" />
    <path d="M7 18a5 5 0 0 1 10 0" />
    <path d="M12 6v4M5.5 10.5l1.6 1.6M18.5 10.5l-1.6 1.6" />
  </G>
)

export const MailGlyph = ({ dashed, ...p }: DayIconProps & { dashed?: boolean }) => (
  <G {...p}>
    {dashed ? (
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" strokeDasharray="3 2.5" />
    ) : (
      <>
        <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </>
    )}
  </G>
)

export const DocGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5" />
    <path d="M10 13h6M10 17h4" />
  </G>
)

export const BellGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </G>
)

export const Shield = (p: DayIconProps) => (
  <G {...p}>
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
  </G>
)

export const ClockGlyph = (p: DayIconProps) => (
  <G {...p}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4l3 2" />
  </G>
)

export const CheckGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="m5 12 4 4 10-10" />
  </G>
)

export const MoonGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
  </G>
)

export const XGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </G>
)

export const External = (p: DayIconProps) => (
  <G {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M18 14v5H5V6h5" />
  </G>
)

export const Dots = (p: DayIconProps) => (
  <G {...p}>
    <circle cx="6" cy="12" r="0.8" />
    <circle cx="12" cy="12" r="0.8" />
    <circle cx="18" cy="12" r="0.8" />
  </G>
)

export const Sparkle = (p: DayIconProps) => (
  <G {...p}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
  </G>
)

export const ChevronRight = (p: DayIconProps) => (
  <G {...p}>
    <path d="m9 6 6 6-6 6" />
  </G>
)

export const ChevronLeft = (p: DayIconProps) => (
  <G {...p}>
    <path d="m15 6-6 6 6 6" />
  </G>
)

export const ChevronDown = (p: DayIconProps) => (
  <G {...p}>
    <path d="m6 9 6 6 6-6" />
  </G>
)

export const CalendarGlyph = (p: DayIconProps) => (
  <G {...p}>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M4 10h16" />
  </G>
)

export const ArrowRight = (p: DayIconProps) => (
  <G {...p}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </G>
)

export const Refresh = (p: DayIconProps) => (
  <G {...p}>
    <path d="M4 12a8 8 0 1 0 2.3-5.6" />
    <path d="M4 4v4h4" />
  </G>
)

export const Plus = (p: DayIconProps) => (
  <G {...p}>
    <path d="M12 5v14M5 12h14" />
  </G>
)

export const Minus = (p: DayIconProps) => (
  <G {...p}>
    <path d="M5 12h14" />
  </G>
)

export const BrokenLink = (p: DayIconProps) => (
  <G {...p}>
    <path d="M9 7H7a5 5 0 0 0 0 10h2" />
    <path d="M15 7h2a5 5 0 0 1 3.5 8.5" />
    <path d="M4 4l16 16" />
  </G>
)

export const CapGlyph = (p: DayIconProps) => (
  <G {...p}>
    <path d="M2 9l10-5 10 5-10 5z" />
    <path d="M6 11v5c3 2 9 2 12 0v-5" />
  </G>
)
